import type { Plan } from '../data/plans';
import { PLAN_FEATURES } from '../data/plans';

interface PlanCardProps {
  plan: Plan;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}

export function PlanCard({ plan, selected, onSelect, disabled = false }: PlanCardProps) {
  const isPopular = plan.badge === 'MOST POPULAR';
  return (
    <div
      className={`relative rounded-2xl border-2 bg-white p-6 sm:p-7 transition-all duration-100 flex flex-col ${
        selected
          ? 'border-ink shadow-[4px_4px_0_0_rgba(15,18,23,1)]'
          : 'border-ink/10 hover:border-ink/30'
      } ${isPopular ? 'sm:scale-[1.03] sm:shadow-cardLg' : ''}`}
    >
      {plan.badge && (
        <span
          className={`absolute -top-3 left-6 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${
            isPopular
              ? 'bg-ink text-white'
              : 'bg-brand text-ink border-2 border-ink'
          }`}
        >
          {plan.badge}
        </span>
      )}

      <h3 className="text-lg font-bold text-ink mb-1">{plan.name}</h3>
      <p className="text-ink-soft text-sm mb-5">{plan.blurb}</p>

      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-display-sm font-extrabold text-ink">{plan.monthlyEquivalent}</span>
        <span className="text-ink-soft text-sm">{plan.cadence}</span>
      </div>
      <p className={`text-sm font-semibold text-ink-soft ${plan.savings ? 'mb-2' : 'mb-6'}`}>
        Just <span className="text-ink font-bold">{plan.perDay}</span> a day
      </p>
      {plan.savings && (
        <p className="inline-block self-start rounded-full bg-teal-100 text-teal-800 text-xs font-bold px-2 py-0.5 mb-6">
          {plan.savings}
        </p>
      )}

      <ul className="space-y-2 mb-7 flex-1">
        {PLAN_FEATURES.map((f) => (
          <li key={f.label} className="flex items-start gap-2.5 text-sm text-ink">
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden className="flex-shrink-0 mt-0.5"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="leading-snug">{f.label}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        className="btn-primary"
      >
        Choose this plan →
      </button>
    </div>
  );
}
